// Capa de acceso a datos del módulo Empresa (perfil institucional). Los
// datos "administrativos" de la empresa (nombre, subdominio, estado, plan)
// son de solo lectura acá — su edición es responsabilidad del Super Admin
// (ver el comentario de cabecera en panel-super-admin/vistas/empresas.js);
// lo único editable desde el Panel Cliente son los datos de `empresa_perfil`
// (logo, descripción, contacto, redes, horario), que son responsabilidad
// del propio admin_empresa.

import { supabaseClient } from '@plataforma/supabase-client';

import { obtenerEstadoSesion } from '../estado-sesion.js';

function empresaIdActual() {
  return obtenerEstadoSesion().claims.empresa_id;
}

/**
 * Normaliza el perfil de empresa venga como venga dentro del embed
 * `empresa_perfil` de las consultas a `empresa`:
 *
 *   - OBJETO → relación 1:1 (`empresa_perfil.empresa_id` es PRIMARY KEY; en
 *     runtime PostgREST la entrega como OBJETO, no como array).
 *   - ARRAY  → forma to-many (se toma el primer elemento).
 *   - null/undefined → no hay fila de perfil (se devuelve {}).
 *
 * @param {object|object[]|null|undefined} empresaPerfil
 * @returns {object}
 */
export function normalizarPerfilEmpresa(empresaPerfil) {
  if (Array.isArray(empresaPerfil)) {
    return empresaPerfil[0] ?? {};
  }
  return empresaPerfil ?? {};
}

/**
 * Empresa + su plan (solo lectura) + su perfil (editable) + trial,
 * todo en una sola consulta — es exactamente lo que la vista "Mi empresa"
 * y el dashboard necesitan pintar de una vez.
 */
export async function obtenerEmpresaActual() {
  const { data: empresa, error } = await supabaseClient()
    .from('empresa')
    .select(
      'id, nombre, slug, subdominio, estado, ' +
        'trial_inicia_en, trial_fin_en, trial_estado, ' +
        'periodo_activo_fin, ' +
        'plan_pendiente_id, ' +
        'plan!empresa_plan_id_fkey (' +
        '  id, nombre, precio_mensual, limite_productos, limite_imagenes, ' +
        '  plan_feature (*), ' +
        '  plan_limite (*)' +
        '), ' +
        'plan_pendiente:plan_pendiente_id (' +
        '  id, nombre, precio_mensual' +
        '), ' +
        'suscripcion (' +
        '  id, estado, plan_id, inicia_en, fin_en, ' +
        '  plan (id, nombre, precio_mensual, plan_feature (*), plan_limite (*))' +
        '), ' +
        'solicitud_contratacion (*), ' +
        'empresa_perfil (*)',
    )
    .eq('id', empresaIdActual())
    .single();

  if (error || !empresa) {
    return { data: empresa, error };
  }

  // El embed `empresa_perfil` puede venir como objeto (1:1, forma observada
  // en runtime), como array, o como null sin fila de perfil. Se normaliza a
  // un perfil PLANO `empresa.perfil` que consumen el EditorState, el preview
  // y las vistas, idéntico a lo que `actualizarPerfilEmpresa` guarda.
  const perfil = normalizarPerfilEmpresa(empresa.empresa_perfil);

  let planPrueba = null;
  if (empresa.estado === 'en_prueba' || empresa.trial_estado === 'activa') {
    const { data: planes } = await supabaseClient()
      .from('plan')
      .select(
        'id, nombre, precio_mensual, limite_productos, limite_imagenes, plan_feature (*), plan_limite (*)',
      )
      .eq('nombre', 'Prueba')
      .eq('estado', 'activo')
      .limit(1);
    planPrueba = planes?.[0] ?? null;
  }

  return { data: { ...empresa, perfil, plan_prueba: planPrueba }, error: null };
}

/**
 * Guarda el perfil institucional. Usa UPSERT sobre `empresa_id` (PRIMARY KEY,
 * fila única por empresa) en lugar de UPDATE directo: si la empresa aún no
 * tiene fila de perfil, esta se CREA automáticamente. El UPDATE clásico con
 * `.select().single()` devolvía 406 (PGRST116) en producción cuando el UPDATE
 * afectaba 0 filas (empresa nueva sin perfil) — con upsert la operación
 * siempre toca exactamente 1 fila y los errores reales de la BD se propagan
 * tal cual.
 *
 * @param {object} cambios - subconjunto de columnas de empresa_perfil
 *   (logo_url, descripcion, whatsapp, correo, direccion, facebook,
 *   instagram, tiktok, youtube, horario, etc.)
 */
export async function actualizarPerfilEmpresa(cambios) {
  return supabaseClient()
    .from('empresa_perfil')
    .upsert({ empresa_id: empresaIdActual(), ...cambios }, { onConflict: 'empresa_id' })
    .select()
    .maybeSingle();
}

/**
 * Normaliza un número de WhatsApp al formato canónico que consume el panel y
 * el storefront (`enlaceWhatsApp` quita cualquier no dígito y arma
 * `wa.me/<digitos>`). Conserva un `+` inicial si el usuario lo escribió, el
 * resto se deja como solo dígitos.
 *
 * @param {string|null|undefined} valor
 * @returns {string} número normalizado ('' si viene vacío)
 */
export function normalizarWhatsapp(valor) {
  if (typeof valor !== 'string') return '';
  const limpio = valor.trim();
  if (!limpio) return '';
  const conMas = limpio.startsWith('+');
  const digitos = limpio.replace(/^\+/, '').replace(/\D/g, '');
  return `${conMas ? '+' : ''}${digitos}`;
}

/**
 * El número debe tener al menos 8 dígitos (prefijo de país + número local) y
 * a lo sumo 15 (máximo E.164). No exige `+` — la UI indica el formato
 * esperado y normaliza al guardar.
 * @param {string|null|undefined} valor
 * @returns {boolean}
 */
export function esWhatsappValido(valor) {
  const normalizado = normalizarWhatsapp(valor);
  const digitos = normalizado.replace(/\D/g, '');
  return digitos.length >= 8 && digitos.length <= 15;
}

/**
 * Valida que un enlace sea http(s). Un valor vacío es válido (campo opcional).
 * @param {string|null|undefined} valor
 * @returns {boolean}
 */
export function esUrlHttp(valor) {
  if (!valor || !String(valor).trim()) return true;
  try {
    const url = new URL(String(valor).trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export const DIAS_SEMANA = [
  'lunes',
  'martes',
  'miercoles',
  'jueves',
  'viernes',
  'sabado',
  'domingo',
];

/**
 * Estructura segura de horario: 7 días × { abre, cierra, cerrado }. Tolera
 * que venga como `{}` (defaults) o con días parciales. Los horarios vacíos
 * caen a 09:00–18:00 y el flag `cerrado` se interpreta como booleano.
 * @param {object|null|undefined} horario
 * @returns {object}
 */
export function normalizarHorario(horario) {
  const entrada = horario && typeof horario === 'object' ? horario : {};
  const resultado = {};
  for (const d of DIAS_SEMANA) {
    const dia = entrada[d] ?? {};
    resultado[d] = {
      abre: typeof dia.abre === 'string' && dia.abre ? dia.abre : '09:00',
      cierra: typeof dia.cierra === 'string' && dia.cierra ? dia.cierra : '18:00',
      cerrado: Boolean(dia.cerrado),
    };
  }
  return resultado;
}

export async function actualizarNombreEmpresa(nombre) {
  return supabaseClient()
    .from('empresa')
    .update({ nombre })
    .eq('id', empresaIdActual())
    .select('id, nombre')
    .single();
}
